// Sends "page_translated" once per page load when the browser / a proxy translates the page.
// Keep in sync with apps/auth/analytics/pageTranslation.ts
import { sendGtEvent } from 'apps/promo/analytics/analytics';
import { GA4_PAGE_TRANSLATED } from 'apps/promo/constants/GoogleAnalyticsEvents';
import { getTranslationInfo } from 'apps/promo/helpers/translationInfo';

// translators change <html> class and lang in separate steps: coalesce them so "to" is already the target lang
export const PAGE_TRANSLATED_DEBOUNCE_MS = 500;

let isReported = false;

const noop = (): void => {};

const reportIfTranslated = (): boolean => {
    const translation = getTranslationInfo();
    if (!translation || isReported) {
        return false;
    }
    isReported = true;
    sendGtEvent(GA4_PAGE_TRANSLATED, { ...translation });
    return true;
};

export const trackPageTranslation = (): (() => void) => {
    try {
        if (isReported || typeof MutationObserver === 'undefined') {
            return noop;
        }
        // already translated before hydration (e.g. *.translate.goog proxy)
        if (reportIfTranslated()) {
            return noop;
        }

        let timerId: ReturnType<typeof setTimeout> | undefined;
        const observer = new MutationObserver(() => {
            clearTimeout(timerId);
            timerId = setTimeout(() => {
                if (reportIfTranslated()) {
                    observer.disconnect();
                }
            }, PAGE_TRANSLATED_DEBOUNCE_MS);
        });
        // only <html> attributes: cheap, no subtree observation of the whole app
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'lang'] });

        return () => {
            clearTimeout(timerId);
            observer.disconnect();
        };
    } catch {
        return noop;
    }
};

// for tests only
export const resetPageTranslationTracking = (): void => {
    isReported = false;
};
